import { Injectable } from '@angular/core';
import firebase from 'firebase/app';
import 'firebase/messaging';
import 'firebase/firestore';

@Injectable({
  providedIn: 'root'
})
export class FcmNotificationService {
  private messaging: firebase.messaging.Messaging;
  private currentToken: string | null = null;

  constructor() {
    // Initialize Firebase Messaging
    if (firebase.messaging.isSupported()) {
      this.messaging = firebase.messaging();
      console.log('[FCM Service] Firebase Messaging initialized');
    } else {
      console.warn('[FCM Service] Firebase Messaging is not supported in this browser');
    }
  }

  /**
   * Request notification permission and get FCM token
   */
  async requestPermissionAndGetToken(): Promise<string | null> {
    try {
      // Check if messaging is supported
      if (!firebase.messaging.isSupported()) {
        console.warn('[FCM Service] Push notifications are not supported in this browser');
        return null;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      console.log('[FCM Service] Notification permission:', permission);

      if (permission !== 'granted') {
        console.warn('[FCM Service] Notification permission denied');
        return null;
      }

      // Get FCM token
      // ⚠️ IMPORTANT: Replace with YOUR REAL VAPID KEY from Firebase Console!
      //
      // How to get it:
      // 1. Go to: https://console.firebase.google.com/project/parkbot-ph-prd/settings/cloudmessaging
      // 2. Scroll to "Web Push certificates"
      // 3. Click "Generate key pair" (if you don't have one)
      // 4. Copy the key (starts with 'B', ~88 characters long)
      // 5. Replace the placeholder below
      //
      // See docs/GET-VAPID-KEY.md for step-by-step guide with screenshots
      const token = await this.messaging.getToken({
        vapidKey: 'BA3H9llDqBjbHpJpiAs4LQRNmF2mzbVR6JeKv6g8S3PcAKS5a0Qfm8cry7uGFC8JD_B0x8OA8FsrC1gLdJmW0FU'  // ← REPLACE THIS!
      });

      if (token) {
        console.log('[FCM Service] FCM Token obtained:', token);
        this.currentToken = token;
        return token;
      } else {
        console.warn('[FCM Service] No registration token available');
        return null;
      }
    } catch (error) {
      // Check if it's a service worker registration error (common in development)
      if (error?.code === 'messaging/failed-service-worker-registration') {
        console.warn('[FCM Service] Service worker not available. This is normal in development mode.');
        console.warn('[FCM Service] Push notifications will work after building and deploying the app.');
        console.warn('[FCM Service] For local testing, run: npm run build && serve the dist folder');
      } else {
        console.error('[FCM Service] Error getting permission and token:', error);
      }
      return null;
    }
  }

  /**
   * Save FCM token to Firestore for the user
   */
  async saveFcmTokenToFirestore(userId: string, token: string): Promise<void> {
    try {
      const userRef = firebase.firestore().collection('users').doc(userId);
      const userDoc = await userRef.get();

      const newTokenData = {
        token: token,
        device_type: this.getDeviceType(),
        browser: this.getBrowserInfo(),
        created_at: new Date(),
        last_used: new Date()
      };

      if (userDoc.exists) {
        const existingTokens = userDoc.data()?.fcm_tokens || [];

        // Check if token already exists
        const tokenIndex = existingTokens.findIndex((t: any) => t.token === token);

        if (tokenIndex >= 0) {
          // Update existing token's last_used
          existingTokens[tokenIndex].last_used = new Date();
          await userRef.update({ fcm_tokens: existingTokens });
        } else {
          // Add new token
          existingTokens.push(newTokenData);
          await userRef.update({ fcm_tokens: existingTokens });
        }
      } else {
        // Create new document
        await userRef.set({
          fcm_tokens: [newTokenData]
        }, { merge: true });
      }

      console.log('[FCM Service] FCM token saved to Firestore for user:', userId);
    } catch (error) {
      console.error('[FCM Service] Error saving FCM token to Firestore:', error);
      throw error;
    }
  }

  /**
   * Save FCM token for public users (MyParking)
   */
  async saveFcmTokenForPublicUser(phoneNumber: string, token: string): Promise<void> {
    try {
      const tokenRef = firebase.firestore()
        .collection('public_user_fcm_tokens')
        .doc(phoneNumber);

      const tokenDoc = await tokenRef.get();

      const newTokenData = {
        token: token,
        device_type: this.getDeviceType(),
        browser: this.getBrowserInfo(),
        created_at: new Date(),
        last_used: new Date()
      };

      if (tokenDoc.exists) {
        const existingTokens = tokenDoc.data()?.tokens || [];

        // Check if token already exists
        const tokenIndex = existingTokens.findIndex((t: any) => t.token === token);

        if (tokenIndex >= 0) {
          // Update existing token's last_used
          existingTokens[tokenIndex].last_used = new Date();
          await tokenRef.update({
            tokens: existingTokens,
            phone_number: phoneNumber
          });
        } else {
          // Add new token
          existingTokens.push(newTokenData);
          await tokenRef.update({
            tokens: existingTokens,
            phone_number: phoneNumber
          });
        }
      } else {
        // Create new document
        await tokenRef.set({
          phone_number: phoneNumber,
          tokens: [newTokenData]
        });
      }

      console.log('[FCM Service] FCM token saved for public user:', phoneNumber);
    } catch (error) {
      console.error('[FCM Service] Error saving FCM token for public user:', error);
      throw error;
    }
  }

  /**
   * Listen for foreground messages
   */
  onForegroundMessage(callback: (payload: any) => void): void {
    if (!firebase.messaging.isSupported()) {
      return;
    }

    this.messaging.onMessage((payload) => {
      console.log('[FCM Service] Foreground message received:', payload);
      callback(payload);
    });
  }

  /**
   * Delete FCM token (call when user logs out)
   */
  async deleteToken(): Promise<void> {
    try {
      if (!firebase.messaging.isSupported() || !this.currentToken) {
        return;
      }

      await this.messaging.deleteToken();
      this.currentToken = null;
      console.log('[FCM Service] FCM token deleted');
    } catch (error) {
      console.error('[FCM Service] Error deleting FCM token:', error);
    }
  }

  /**
   * Remove FCM token from Firestore
   */
  async removeFcmTokenFromFirestore(userId: string, token: string): Promise<void> {
    try {
      const userDoc = await firebase.firestore()
        .collection('users')
        .doc(userId)
        .get();

      if (userDoc.exists) {
        const userData = userDoc.data();
        const fcmTokens = userData?.fcm_tokens || [];

        // Remove the specific token
        const updatedTokens = fcmTokens.filter((t: any) => t.token !== token);

        await firebase.firestore()
          .collection('users')
          .doc(userId)
          .update({
            fcm_tokens: updatedTokens
          });

        console.log('[FCM Service] FCM token removed from Firestore');
      }
    } catch (error) {
      console.error('[FCM Service] Error removing FCM token from Firestore:', error);
    }
  }

  /**
   * Get current FCM token
   */
  getCurrentToken(): string | null {
    return this.currentToken;
  }

  /**
   * Get device type
   */
  private getDeviceType(): string {
    const ua = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(ua)) {
      return 'tablet';
    }
    if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }

  /**
   * Get browser information
   */
  private getBrowserInfo(): string {
    const ua = navigator.userAgent;
    let browserName = 'Unknown';

    if (ua.indexOf('Firefox') > -1) {
      browserName = 'Firefox';
    } else if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) {
      browserName = 'Opera';
    } else if (ua.indexOf('Trident') > -1) {
      browserName = 'IE';
    } else if (ua.indexOf('Edge') > -1) {
      browserName = 'Edge';
    } else if (ua.indexOf('Chrome') > -1) {
      browserName = 'Chrome';
    } else if (ua.indexOf('Safari') > -1) {
      browserName = 'Safari';
    }

    return browserName;
  }

  /**
   * Check if browser supports push notifications
   */
  isSupported(): boolean {
    return firebase.messaging.isSupported() && 'Notification' in window;
  }
}
